import { apiClient } from '@/shared/api';
import type { NormalizedApiError } from '@/shared/api';

import type {
  BulkQuickSetStockRequest,
  BulkQuickSetStockResponse,
  ProductStockItemListResponse,
  ProductStockLocation,
  QuickSetStockRequest,
  QuickSetStockResponse,
} from '../model/productStockTypes';

/**
 * Catalog-owned client for the inventory-backed stock surfaces of the catalog pages. Calls the
 * SAME `web-merchant-portal-bff` Inventory endpoints `features/inventory` uses, but is a local,
 * catalog-owned copy (FSD forbids importing across features) scoped to what the catalog needs:
 * the product-360 read-only snapshot, the location lookup, and — since plan-catalog-007 replaced
 * the master stock write — the quick/bulk SET-TO-VALUE stock edits (per location, applied by the
 * BFF as ledger-audited MANUAL_CORRECTION inventory adjustments).
 */

/**
 * `GET /api/v1/inventory/stock/{productId}` — every location's stock snapshot for one product.
 *
 * A product with no stock at all (created in the catalogue, never received into a warehouse) is a
 * NORMAL state that must render as "no stock yet", never as an error. `web-inventory-service` now
 * returns `200 {items: []}` for it (user-story-fulfilment-007 AC6), but it used to answer `404`,
 * which the BFF propagates verbatim — and the portal ships independently of the backend, so a
 * 404 is still mapped to an empty list here. Keep this guard even after every contour is upgraded:
 * it costs nothing and makes the catalog surfaces immune to a rollback of that service.
 */
export const getProductStock = async (productId: string): Promise<ProductStockItemListResponse> => {
  try {
    return (await apiClient.get<ProductStockItemListResponse>(`/api/v1/inventory/stock/${productId}`))
      .data;
  } catch (error) {
    if ((error as NormalizedApiError)?.status === 404) {
      return { items: [] };
    }
    throw error;
  }
};

/** `POST /api/v1/inventory/stock/quick-set` — set the counted on-hand value at one location. */
export const quickSetStock = async (body: QuickSetStockRequest): Promise<QuickSetStockResponse> =>
  (await apiClient.post<QuickSetStockResponse>('/api/v1/inventory/stock/quick-set', body)).data;

/** `POST /api/v1/inventory/stock/quick-set/bulk` — the same for an explicit selection (≤ 100). */
export const bulkQuickSetStock = async (
  body: BulkQuickSetStockRequest,
): Promise<BulkQuickSetStockResponse> =>
  (await apiClient.post<BulkQuickSetStockResponse>('/api/v1/inventory/stock/quick-set/bulk', body))
    .data;

interface StoreLocationSearchRequestDto {
  size?: number;
}

interface StoreLocationPageResponseDto {
  items: ProductStockLocation[];
}

/** `StoreLocationSearchRequest.size` caps at 100 — matches the BFF-enforced maximum. */
const MAX_SEARCH_SIZE = 100;

/**
 * Fetches up to {@link MAX_SEARCH_SIZE} tenant locations (active and inactive — a stock row may
 * reference a location that has since been deactivated) for an id→name lookup. Same endpoint
 * `features/inventory/api/locationsApi.ts` uses — see that file's own javadoc-style comment for why
 * this is a local duplicate, not a cross-feature import.
 */
export const searchAllLocations = async (): Promise<ProductStockLocation[]> => {
  const body: StoreLocationSearchRequestDto = { size: MAX_SEARCH_SIZE };
  const { data } = await apiClient.post<StoreLocationPageResponseDto>(
    '/api/v1/store/locations/search',
    body,
  );
  return data.items;
};
